/**
 * app.js — /photo-translate
 *
 * Pipeline: image → PP-OCRv5 (detect + recognise, in a worker) → block merge →
 * TranslatorEngine → erase + re-set the type on a canvas → downloadable picture.
 *
 * Design notes that came out of measurement rather than taste:
 *
 *  • Recognition streams. Prod telemetry for /ocr-text-extractor puts 1 589 of
 *    2 266 monthly abandons on the `processing` stage — people leave a silent
 *    tab. Every recognised line is painted the moment it lands.
 *  • The engine downloads 12–20 MB on first use and nothing on later visits
 *    (Cache API). The size is stated BEFORE the download starts, because an
 *    unexplained wait is the same abandon in a different costume.
 *  • Blocks, not boxes, are translated: sending "Grilled" and "salmon" to a
 *    translator separately returns two unrelated words.
 *  • Every automatic decision is editable afterwards. OCR on a photo is never
 *    perfect and a tool that cannot be corrected is a demo.
 *
 * View layer, rewritten 2026-09-12 together with the page:
 *
 *  • ONE stage slot with four states on `data-state` (intake / camera / work /
 *    result) instead of four stacked sections. Measured before the change: the
 *    upload zone ended BELOW THE FOLD on every screen size tested, from
 *    1920×1080 (by 1 px) to 1024×768 (by 491 px).
 *  • The correction surface moved ONTO the picture. Every recognised block is a
 *    focusable frame over the canvas; clicking it opens the editor next to the
 *    text it edits. The rail list stays — it is the keyboard path and the only
 *    way to see all the strings at once — and both write the same state.
 *  • The result is zoomable and pannable. A dense menu rendered to fit a
 *    900 px column is a picture of a menu, not a readable one.
 */
(function () {
    'use strict';

    var CFG = window.__PT_CFG || {};
    var T = CFG.i18n || {};
    var t = function (k, d) { return T[k] || d || k; };

    // ── «Точный перевод»: серверный лейн (js/translate-server-lane.js) ────
    // Дверь Премиума держит центральный гейт; `gated` — лейн внесён в реестр
    // server-lane, иначе страница двери не рисует.
    var LANE = CFG.precise || null;
    // «Рукописный и сложный шрифт» (js/photo-translate/vision.js); null — лейн выключен.
    var VISION = CFG.vision || null;
    function isPro() { return !!(window.__toolLimit && window.__toolLimit.isPro && window.__toolLimit.isPro()); }
    // Phone or not, on the events that carry timings. A second recogniser is
    // paid in seconds on a phone, and those seconds are knowable only from the
    // field: CDP CPU throttling does not reach the OCR worker (measured 16.09).
    var MOB = /Mobi|Android|iPhone|iPad/i.test((window.navigator && navigator.userAgent) || '') ? 1 : 0;
    // С ценой за токены (LANE.tokens) дверь решает сервер: Премиум в лимите,
    // остальным — предложение «за N токенов». Без цены — прежняя дверь Премиума.
    function preciseOpen() { return !!(LANE && window.__translateLane) && (!LANE.gated || LANE.tokens > 0 || isPro()); }
    function quality() {
        var r = document.querySelector('input[name="ptQuality"]:checked');
        return (r && r.value === 'precise' && preciseOpen()) ? 'precise' : 'standard';
    }
    function fmtN(n) { try { return Number(n).toLocaleString(window.__LOCALE || undefined); } catch (_) { return String(n); } }
    function planText(key, dflt) {
        return t(key, dflt).split(':plan').join((LANE && LANE.plan) || 'Premium');
    }
    function upsellNote(node) {
        if (!node || !LANE) return;
        node.classList.remove('good');
        node.classList.add('warn');
        node.textContent = planText('precise_upsell', 'Точный перевод входит в :plan') + ' ';
        if (LANE.pricingUrl) {
            var a = document.createElement('a');
            a.href = LANE.pricingUrl;
            a.target = '_blank';
            a.rel = 'noopener';
            a.textContent = planText('precise_see_plan', 'Что даёт :plan');
            node.appendChild(a);
        }
        node.hidden = false;
        funnel('precise_upsell', { where: node.id });
    }
    function syncPlanBadge() {
        var b = $('ptPrecisePlan');
        if (b) b.hidden = !LANE || !LANE.gated || LANE.tokens > 0 || isPro();
    }
    function applySavedQuality() {
        if (!LANE || !window.__toolSettings || state.busy) return;
        var want = window.__toolSettings.load('photo-translate', 'quality', 'standard');
        setQuality(want === 'precise' && preciseOpen() ? 'precise' : 'standard', true);
    }

    var el = {};
    var state = {
        files: [],          // {id, file, name, base, canvas, blocks, done, …}
        active: -1,
        engine: null,
        engineLang: null,
        busy: false,
        limitTicket: null,
        abort: null,
        sel: -1,            // выбранный блок
        zoom: 1, fitZoom: 1, panX: 0, panY: 0,
        compare: false,
        camera: null,
    };

    // ── small helpers ────────────────────────────────────────────────────
    var $ = function (id) { return document.getElementById(id); };
    var toast = function (m, k) { if (window.__toast) window.__toast(m, k || 'info'); };
    var fail = function (e, ctx) { if (window.__handleError) window.__handleError(e, ctx); else console.error(ctx, e); };
    function funnel(stage, data) { try { if (window.__aeFunnel) window.__aeFunnel(stage, data || {}); } catch (_) { } }
    function stage(name, data) { try { if (window.__stage) window.__stage(name, data || {}); } catch (_) { } }

    /** A hard-coded decimal comma reads as a typo on eight of the nine locales. */
    function fmtMb(n) {
        var v = Math.round(n * 10) / 10;
        try { return v.toLocaleString(window.__LOCALE || undefined); } catch (_) { return String(v); }
    }

    function setView(name) {
        if (el.app) el.app.dataset.state = name;
    }

    // ── engine lifecycle ─────────────────────────────────────────────────

    /**
     * "Detect automatically" is ONE engine with two recognisers — the Latin
     * reading of every line plus the Cyrillic one as `alt` — and not a guess
     * followed by a re-read. Why, with the numbers: script-pick.js.
     */
    var DUAL = { lang: 'en', also: 'ru' };

    async function ensureEngine(lang) {
        if (state.engine && state.engineLang === lang) return state.engine;
        if (state.engine) { state.engine.close(); state.engine = null; }

        var dual = lang === 'auto';
        var openLang = dual ? DUAL.lang : lang;
        var also = dual ? DUAL.also : undefined;
        var script = dual ? 'latin+eslav' : window.PPOCR.scriptFor(lang);
        var cached = await window.PPOCR.isCached(openLang, also);
        if (!cached) {
            var mb = window.PPOCR.downloadMb(openLang, false, also);
            setPhase('download', t('phase_download', 'Загрузка модели') + ' · ' + fmtMb(mb) + ' МБ');
            funnel('model_download_start', { script: script, mb: mb, mob: MOB });
        }
        var t0 = performance.now();
        var eng = await window.PPOCR.open(openLang, {
            onProgress: function (f) { setProgress(f * 0.999); },
            // Versioned by the asset router; /vendor is immutable-cached and a
            // worker parked there would never receive a fix.
            workerUrl: CFG.workerUrl || undefined,
            alsoLang: also,
        });
        state.engine = eng;
        state.engineLang = lang;
        funnel('engine_ready', {
            script: script, provider: eng.provider, cached: cached,
            ms: Math.round(performance.now() - t0), mob: MOB,
        });
        return eng;
    }

    // ── phases / progress ────────────────────────────────────────────────

    function setPhase(key, label) {
        if (el.phase) el.phase.textContent = label;
        if (el.work) el.work.dataset.phase = key;
    }
    function setProgress(f) {
        if (!el.bar) return;
        el.bar.style.width = Math.max(0, Math.min(1, f)) * 100 + '%';
    }

    // ── main run ─────────────────────────────────────────────────────────

    /** One read pass with a given source language. */
    async function readWith(item, lang, bitmap) {
        var eng = await ensureEngine(lang);
        setPhase('read', t('phase_read', 'Чтение текста'));
        var res = await eng.read(bitmap, {
            onBoxes: function (m) { item.boxCount = m.boxes.length; },
            onLine: function (line) {
                if (line.total) setProgress(0.05 + 0.45 * (line.index / line.total));
                appendLiveLine(line.alt && window.PTScriptPick ? window.PTScriptPick.pickLine(line).text : line.text);
            },
        });
        // Mean CTC score across the read. Only comparable WITHIN one model, but
        // that is exactly the comparison "is this the right script?" needs.
        var confs = res.lines.map(function (l) { return l.conf; }).filter(function (v) { return isFinite(v); });
        res.meanConf = confs.length ? confs.reduce(function (a, b) { return a + b; }, 0) / confs.length : 0;
        funnel('ocr_done', {
            ms: res.msTotal, boxes: res.boxes, lines: res.lines.length,
            provider: eng.provider, script: eng.script2 ? eng.script + '+' + eng.script2 : eng.script, lang: lang,
            conf: Math.round(res.meanConf * 100) / 100,
            // dual: 'main' | 'alt' when the worker settled on one recogniser, 'both' when
            // the page never looked single-script; solo — lines read by one only.
            det: res.msDet, rec: res.msRec, dual: res.dual ? (res.dualMode || 'both') : 0, solo: res.dualSolo || 0, mob: MOB,
        });
        // A short history of what was read as what, for support: "почему он
        // прочитал это как английский" is answerable from the page, without
        // asking the visitor to reproduce anything. Capped so it cannot grow.
        try {
            var log = (window.__PT_READS = window.__PT_READS || []);
            log.push({ lang: lang, conf: Math.round(res.meanConf * 100) / 100, lines: res.lines.length });
            if (log.length > 20) log.shift();
        } catch (_) { }
        return res;
    }

    /** @param show  false while batching — the caller decides when to reveal. */
    async function runFile(item, show) {
        show = show !== false;
        var srcLang = el.src.value;
        var tgtLang = el.tgt.value;

        setPhase('detect', t('phase_detect', 'Поиск текста'));
        setProgress(0.02);

        var bitmap = await createImageBitmap(item.file);
        item.width = bitmap.width;
        item.height = bitmap.height;

        // Working copy for compositing; the bitmap itself is transferred away.
        var base = document.createElement('canvas');
        base.width = bitmap.width;
        base.height = bitmap.height;
        base.getContext('2d').drawImage(bitmap, 0, 0);
        item.base = base;

        // Without the picker (its script failed to load) "auto" degrades to a
        // plain Latin read — the old first pass — instead of breaking the run.
        var auto = srcLang === 'auto' && !!window.PTScriptPick;
        var usedLang = srcLang === 'auto' ? 'en' : srcLang;
        var res = await readWith(item, auto ? 'auto' : usedLang, bitmap);
        if (auto) {
            var picked = await pickScript(item, res);
            res = picked.res;
            usedLang = picked.lang;
        }
        item.result = res;
        item.readLang = usedLang;

        if (!res.lines.length) {
            setPhase('empty', t('phase_empty', 'Текст не найден'));
            funnel('no_text_found', { w: item.width, h: item.height });
            if (show) showNoText(item);
            return;
        }

        // ── blocks + translation ─────────────────────────────────────────
        var blocks = window.PTCompose.groupBlocks(res.lines);
        item.blocks = blocks;

        setPhase('translate', t('phase_translate', 'Перевод'));
        // The language the text was actually READ as is also the language it
        // should be translated FROM — running detection a second time can and
        // does disagree with the model that produced the characters.
        var srcCode = item.readLang;
        item.srcDetected = srcCode;
        item.mode = quality();
        await translateBlocks(item, srcCode, tgtLang, item.mode);

        // ── compose ──────────────────────────────────────────────────────
        setPhase('compose', t('phase_compose', 'Сборка изображения'));
        compose(item);
        setProgress(1);
        item.done = true;
        if (show) showResult(item);
    }

    /** Draw the translated picture from scratch out of `item.base` + blocks. */
    function compose(item) {
        var out = item.canvas || document.createElement('canvas');
        out.width = item.base.width;
        out.height = item.base.height;
        var ctx = out.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(item.base, 0, 0);

        var rtl = /^(ar|he|fa|ur)/.test(el.tgt.value);

        // Geometry is resolved ONCE per block and cached: measure the ink in
        // original pixels, then re-sample the colours against that tight box.
        // Doing it on the loose detection box erases clean paper and misses the
        // glyph ends — both visible in the picture, neither in a test.
        item.blocks.forEach(function (b) {
            if (b.paint) return;
            var rough = window.PTCompose.sampleColors(ctx, b.box);
            if (!rough) return;
            var tight = window.PTCompose.refineToInk(ctx, b.box, rough.paper) || b.box;
            var colors = window.PTCompose.sampleColors(ctx, tight) || rough;
            b.paint = { box: tight, colors: colors };
        });

        item.blocks.forEach(function (b) {
            if (b.hidden || !b.paint) return;
            window.PTCompose.eraseBox(ctx, b.paint.box, b.paint.colors);
        });
        item.blocks.forEach(function (b) {
            if (b.hidden || !b.paint) return;
            var text = b.edited != null ? b.edited : (b.translated || b.text);
            if (!String(text).trim()) return;
            window.PTCompose.drawText(ctx, b.paint.box, text, b.paint.colors, {
                align: b.align || window.PTCompose.alignFor(b.paint.box, out.width),
                rtl: rtl,
                weight: b.weight || '600',
                color: b.color || null,
                halo: b.paint.colors.flatness < 0.72,
            });
        });
        item.canvas = out;
        return out;
    }

    // ── "detect automatically" ───────────────────────────────────────────────

    /**
     * Which reading of the photo to keep, and the language to translate FROM.
     * The rules and the measurements behind them live in script-pick.js; this
     * runs them and pays for the extra reads they ask for.
     */
    async function pickScript(item, res) {
        var P = window.PTScriptPick;
        var merged = P.mergeDual(res.lines);
        var tried = 0;
        if (merged.weak) {
            for (var i = 0; i < P.FOREIGN.length; i++) {
                var lang = P.FOREIGN[i];
                tried++;
                setPhase('detect', t('phase_relang', 'Похоже, другой язык — перечитываю'));
                if (el.live) el.live.innerHTML = '';
                var again = await createImageBitmap(item.base);
                var r2 = await readWith(item, lang, again);
                if (P.foreignEvidence(r2.lines, window.PPOCR.scriptFor(lang)).ok) {
                    funnel('auto_script', { script: window.PPOCR.scriptFor(lang), weak: 1, tried: tried, mob: MOB });
                    reflectLang(lang);
                    return { res: r2, lang: lang };
                }
            }
        }
        var script = merged.cyrillic ? 'eslav' : 'latin';
        funnel('auto_script', {
            script: script, weak: merged.weak ? 1 : 0, tried: tried,
            cyr: merged.cyr, letters: merged.letters, mob: MOB,
        });
        var out = {
            lines: merged.lines, msDet: res.msDet, msRec: res.msRec, msTotal: res.msTotal,
            rows: res.rows, boxes: res.boxes, dual: res.dual,
            meanConf: merged.lines.length
                ? merged.lines.reduce(function (a, l) { return a + (l.conf || 0); }, 0) / merged.lines.length : 0,
        };
        var lang2 = script === 'eslav' ? 'ru' : 'en';
        if (merged.lines.length) {
            var guess = await detectFrom(window.PTCompose.groupBlocks(merged.lines));
            if (guess && window.PPOCR.scriptFor(guess) === script) lang2 = guess;
        }
        // A Latin reading keeps "auto" on the control, as before; a Cyrillic or
        // foreign one is shown, so the decision is never a hidden one.
        if (script === 'eslav') reflectLang(lang2);
        return { res: out, lang: lang2 };
    }

    function reflectLang(lang) {
        if (el.src.value === 'auto' && el.src.querySelector('option[value="' + lang + '"]')) {
            el.src.value = lang;
            syncSwap();
        }
    }

    // ── source language guessing ─────────────────────────────────────────

    async function detectFrom(blocks) {
        var sample = blocks.slice(0, 6).map(function (b) { return b.text; }).join(' ').slice(0, 400);
        try {
            // detectLanguageAsync answers {lang, ambiguous}. Until 16.09.2026 this
            // read `d.language`, which never exists, so Chrome's detector was
            // asked on every photo and its answer thrown away.
            var d = await window.TranslatorEngine.detectLanguageAsync(sample, { target: el.tgt ? el.tgt.value : undefined });
            if (d && d.lang && !d.ambiguous) return d.lang;
        } catch (_) { }
        // Nobody could tell: the caller keeps the default of the script it
        // read (latin → en, eslav → ru), as before — a second script-level
        // guess here only repeated it.
        return '';
    }

    // ── live text feed (the anti-abandon surface) ────────────────────────

    function appendLiveLine(text) {
        if (!el.live) return;
        var d = document.createElement('div');
        d.className = 'pt-live-l';
        d.textContent = text;
        el.live.appendChild(d);
        el.live.scrollTop = el.live.scrollHeight;
    }

    // ── views ────────────────────────────────────────────────────────────

    function showWork() {
        setView('work');
        if (el.live) el.live.innerHTML = '';
        setProgress(0);
    }

    function showNoText(item) {
        setView('result');
        if (el.empty) el.empty.hidden = false;
        if (el.view) el.view.hidden = true;
        if (el.actions) el.actions.hidden = false;
        if (el.stripPanel) el.stripPanel.hidden = true;
        if (el.railResult) el.railResult.hidden = true;
        // Save/copy make no sense with nothing recognised; only "another photo".
        ['ptDownload', 'ptDownloadJpg', 'ptCopy', 'ptCopyOrig', 'ptPrecise'].forEach(function (id) {
            var b = $(id); if (b) b.hidden = true;
        });
    }

    function showResult(item) {
        setView('result');
        if (el.empty) el.empty.hidden = true;
        if (el.view) el.view.hidden = false;
        if (el.actions) el.actions.hidden = false;
        ['ptDownload', 'ptDownloadJpg', 'ptCopy', 'ptCopyOrig'].forEach(function (id) {
            var b = $(id); if (b) b.hidden = false;
        });
        if (el.railResult) {
            el.railResult.hidden = false;
            if (el.readAs && el.readLang) {
                el.readAs.hidden = false;
                if (el.readLang.querySelector('option[value="' + item.readLang + '"]')) {
                    el.readLang.value = item.readLang;
                }
            }
        }
        renderStrip();
        state.compare = false;
        syncCompare();
        paintResult(item);
        fitView();
        renderBlockList(item);
        stage('result_shown', {});
        funnel('result_shown', { blocks: item.blocks.length, files: state.files.length });
        renderPrecise(item);
        renderVision(item);
        renderSameLang(item);
    }

    /** File switcher — only meaningful past one file, so hidden until then. */
    function renderStrip() {
        if (!el.strip || !el.stripPanel) return;
        var done = state.files.filter(function (f) { return f.done; });
        if (state.files.length < 2) { el.stripPanel.hidden = true; return; }
        el.stripPanel.hidden = false;
        el.strip.innerHTML = '';
        state.files.forEach(function (f, i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'pt-strip-i';
            b.setAttribute('role', 'tab');
            b.setAttribute('aria-selected', i === state.active ? 'true' : 'false');
            b.disabled = !f.done;

            var n = document.createElement('span');
            n.className = 'pt-strip-n';
            n.textContent = f.name;
            b.appendChild(n);

            var c = document.createElement('span');
            c.className = 'pt-strip-b';
            c.textContent = f.done ? String((f.blocks || []).length) : '…';
            b.appendChild(c);

            b.addEventListener('click', function () {
                if (!f.done) return;
                state.active = i;
                closeEditor();
                funnel('strip_switch', { index: i });
                renderPrecise(f);
                showResult(f);
            });
            el.strip.appendChild(b);
        });
        if (done.length !== state.files.length) {
            // Keep it honest while a batch is still running.
            el.strip.setAttribute('aria-busy', 'true');
        } else {
            el.strip.removeAttribute('aria-busy');
        }
    }

    // ── the picture: paint, zoom, pan, hit frames ────────────────────────

    function paintResult(item) {
        if (!item || !el.canvas) return;
        var src = state.compare ? item.base : (item.canvas || item.base);
        el.canvas.width = src.width;
        el.canvas.height = src.height;
        el.canvas.style.width = src.width + 'px';
        el.canvas.style.height = src.height + 'px';
        el.canvas.getContext('2d').drawImage(src, 0, 0);
        renderHits(item);
    }

    /**
     * A frame over every recognised block.
     *
     * The frames live INSIDE the transformed figure, in image pixels, so they
     * follow zoom and pan for free — no second coordinate system to keep in
     * sync, which is where this class of feature usually breaks.
     */
    function renderHits(item) {
        if (!el.hits) return;
        el.hits.innerHTML = '';
        if (!item || !item.blocks || state.compare) return;
        item.blocks.forEach(function (b, i) {
            var box = (b.paint && b.paint.box) || b.box;
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pt-hit' + (b.hidden ? ' off' : '') + (i === state.sel ? ' on' : '');
            btn.style.left = box.x + 'px';
            btn.style.top = box.y + 'px';
            btn.style.width = Math.max(6, box.w) + 'px';
            btn.style.height = Math.max(6, box.h) + 'px';
            btn.setAttribute('aria-label', t('a11y_edit', 'Правка перевода') + ': ' + b.text);
            btn.addEventListener('click', function (ev) {
                ev.stopPropagation();
                selectBlock(i, btn);
            });
            el.hits.appendChild(btn);
        });
    }

    function applyTransform() {
        if (!el.figure) return;
        el.figure.style.transform =
            'translate(' + state.panX + 'px,' + state.panY + 'px) scale(' + state.zoom + ')';
        // Frame outlines must not thicken with the picture.
        el.figure.style.setProperty('--ptz', String(state.zoom));
        if (el.zoomVal) el.zoomVal.textContent = Math.round(state.zoom * 100) + '%';
        // Only trap touch gestures when there is actually something to pan;
        // otherwise a big picture would swallow the page scroll on a phone.
        var pannable = state.zoom > state.fitZoom * 1.02;
        if (el.view) {
            el.view.style.touchAction = pannable ? 'none' : 'pan-y';
            el.view.dataset.pannable = pannable ? '1' : '0';
        }
    }

    function fitView() {
        var item = state.files[state.active];
        if (!item || !el.view || !el.canvas) return;
        var r = el.view.getBoundingClientRect();
        var w = el.canvas.width || 1, h = el.canvas.height || 1;
        var z = Math.min((r.width - 16) / w, (r.height - 16) / h);
        if (!isFinite(z) || z <= 0) z = 1;
        z = Math.min(z, 1);
        state.fitZoom = z;
        state.zoom = z;
        centre();
    }

    function centre() {
        var r = el.view.getBoundingClientRect();
        state.panX = (r.width - el.canvas.width * state.zoom) / 2;
        state.panY = (r.height - el.canvas.height * state.zoom) / 2;
        applyTransform();
    }

    /** Zoom about a point of the viewport so the pixel under it stays put. */
    function zoomTo(z, cx, cy) {
        z = Math.max(state.fitZoom * 0.9, Math.min(8, z));
        var r = el.view.getBoundingClientRect();
        if (cx == null) { cx = r.width / 2; cy = r.height / 2; }
        var ix = (cx - state.panX) / state.zoom;
        var iy = (cy - state.panY) / state.zoom;
        state.zoom = z;
        state.panX = cx - ix * z;
        state.panY = cy - iy * z;
        clampPan();
        applyTransform();
        closeEditor();
    }

    function clampPan() {
        var r = el.view.getBoundingClientRect();
        var w = el.canvas.width * state.zoom, h = el.canvas.height * state.zoom;
        if (w <= r.width) state.panX = (r.width - w) / 2;
        else state.panX = Math.min(0, Math.max(r.width - w, state.panX));
        if (h <= r.height) state.panY = (r.height - h) / 2;
        else state.panY = Math.min(0, Math.max(r.height - h, state.panY));
    }

    /**
     * Панорамирование и масштаб на сцене.
     *
     * ⚠️⚠️ Захват указателя берётся ТОЛЬКО после того, как палец (или мышь)
     * реально проехал PAN_SLOP пикселей — и это не оптимизация, а условие
     * работоспособности всего, что лежит поверх картинки.
     *
     * `setPointerCapture()` перенаправляет последующие события указателя на
     * захвативший элемент, и `click` после этого приходит НЕ на кнопку, а на
     * сцену. Первая версия захватывала прямо на `pointerdown`, и результат был
     * такой: стоило увеличить снимок — и переставали работать «по размеру»,
     * «показать оригинал», обе кнопки масштаба И рамки правки на самом снимке,
     * то есть ровно то, ради чего увеличивают. Ошибок при этом ноль: событие
     * доставляется, просто не туда.
     *
     * ⚠️ Синтетический `dispatchEvent(new MouseEvent('click'))` этого НЕ видит —
     * он не проходит через захват и «доказывает» исправность. Ловится только
     * настоящим нажатием (Playwright `.click()`), чем и был найден.
     */
    var PAN_SLOP = 4;

    function wireView() {
        if (!el.view) return;
        var pointers = new Map();
        var down = null, panFrom = null, pinchFrom = null, moved = false;

        el.view.addEventListener('pointerdown', function (e) {
            pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            if (pointers.size === 1) {
                down = { id: e.pointerId, x: e.clientX, y: e.clientY, px: state.panX, py: state.panY };
                panFrom = null;
                moved = false;
            } else if (pointers.size === 2) {
                var p = Array.from(pointers.values());
                pinchFrom = { d: dist(p[0], p[1]), z: state.zoom };
                down = null; panFrom = null;
            }
        });
        el.view.addEventListener('pointermove', function (e) {
            if (!pointers.has(e.pointerId)) return;
            pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            if (pinchFrom && pointers.size === 2) {
                var p = Array.from(pointers.values());
                var d = dist(p[0], p[1]);
                if (pinchFrom.d > 0) {
                    var r = el.view.getBoundingClientRect();
                    zoomTo(pinchFrom.z * (d / pinchFrom.d),
                        (p[0].x + p[1].x) / 2 - r.left, (p[0].y + p[1].y) / 2 - r.top);
                }
                e.preventDefault();
                return;
            }
            // Начинаем тащить только когда это уже ТОЧНО перетаскивание, а не
            // нажатие: до этого момента указатель не захвачен и click дойдёт
            // до кнопки или рамки, по которой нажали.
            if (!panFrom && down && e.pointerId === down.id
                && state.zoom > state.fitZoom * 1.02
                && Math.hypot(e.clientX - down.x, e.clientY - down.y) > PAN_SLOP) {
                panFrom = down;
                moved = true;
                el.view.classList.add('is-pan');
                try { el.view.setPointerCapture(e.pointerId); } catch (_) { }
            }
            if (panFrom) {
                state.panX = panFrom.px + (e.clientX - panFrom.x);
                state.panY = panFrom.py + (e.clientY - panFrom.y);
                clampPan();
                applyTransform();
                e.preventDefault();
            }
        });
        ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
            el.view.addEventListener(ev, function (e) {
                pointers.delete(e.pointerId);
                if (pointers.size < 2) pinchFrom = null;
                if (pointers.size === 0) {
                    if (panFrom) el.view.classList.remove('is-pan');
                    down = null; panFrom = null;
                }
            });
        });
        // Перетаскивание закончилось там же, где лежит рамка правки — открывать
        // редактор на этом не надо: человек двигал картинку, а не выбирал надпись.
        el.view.addEventListener('click', function (e) {
            if (!moved) return;
            moved = false;
            e.stopPropagation();
            e.preventDefault();
        }, true);

        /**
         * The wheel zooms only with a modifier (and with the trackpad pinch,
         * which arrives as ctrl+wheel). A plain wheel keeps scrolling the page:
         * a viewer that swallows the scroll wheel is a viewer people scroll
         * past by reloading.
         */
        el.view.addEventListener('wheel', function (e) {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            var r = el.view.getBoundingClientRect();
            zoomTo(state.zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX - r.left, e.clientY - r.top);
        }, { passive: false });

        el.view.addEventListener('dblclick', function (e) {
            var r = el.view.getBoundingClientRect();
            if (state.zoom > state.fitZoom * 1.05) fitView();
            else zoomTo(Math.max(1, state.fitZoom * 2.5), e.clientX - r.left, e.clientY - r.top);
        });
    }

    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

    // ── block editor: on the picture and in the rail ─────────────────────

    function selectBlock(i, anchor) {
        var item = state.files[state.active];
        if (!item || !item.blocks || !item.blocks[i]) return;
        state.sel = i;
        var b = item.blocks[i];
        if (el.popOrig) el.popOrig.textContent = b.text;
        if (el.popText) el.popText.value = b.edited != null ? b.edited : (b.translated || '');
        syncSkipButton(b);
        markSelection();
        openEditor(anchor || el.hits.children[i]);
        funnel('block_open', { index: i, via: anchor ? 'picture' : 'list' });
    }

    function syncSkipButton(b) {
        if (!el.popSkip) return;
        el.popSkip.textContent = b.hidden
            ? t('block_restore', 'Вернуть перевод')
            : t('block_skip', 'Не переводить');
        el.popSkip.setAttribute('aria-pressed', b.hidden ? 'true' : 'false');
    }

    function markSelection() {
        if (el.hits) {
            Array.prototype.forEach.call(el.hits.children, function (n, i) {
                n.classList.toggle('on', i === state.sel);
            });
        }
        if (el.blocks) {
            Array.prototype.forEach.call(el.blocks.children, function (n, i) {
                n.classList.toggle('on', i === state.sel);
            });
        }
    }

    /**
     * The editor is a popover where the browser has one: top layer, light
     * dismiss and Esc arrive for free and cannot be got wrong by hand. Where it
     * is missing the SAME node is shown with a class — position is computed by
     * us in both paths, so behaviour does not fork, only the layer does.
     */
    function openEditor(anchor) {
        if (!el.pop) return;
        var usePopover = typeof el.pop.showPopover === 'function' && el.pop.hasAttribute('popover');
        if (usePopover) { try { el.pop.hidePopover(); } catch (_) { } }
        el.pop.classList.add('is-open');
        if (usePopover) { try { el.pop.showPopover(); } catch (_) { } }
        placeEditor(anchor);
        if (el.popText) { el.popText.focus(); el.popText.select(); }
    }

    function placeEditor(anchor) {
        if (!el.pop) return;
        var pr = el.pop.getBoundingClientRect();
        var w = pr.width || 320, h = pr.height || 150;
        var vw = window.innerWidth, vh = window.innerHeight;
        var ar = anchor ? anchor.getBoundingClientRect()
            : (el.view ? el.view.getBoundingClientRect() : { left: vw / 2, top: vh / 2, width: 0, height: 0, bottom: vh / 2 });
        var left = ar.left + ar.width / 2 - w / 2;
        var top = ar.bottom + 8;
        if (top + h > vh - 8) top = Math.max(8, ar.top - h - 8);
        left = Math.max(8, Math.min(vw - w - 8, left));
        el.pop.style.left = Math.round(left) + 'px';
        el.pop.style.top = Math.round(top) + 'px';
    }

    function closeEditor() {
        if (!el.pop) return;
        el.pop.classList.remove('is-open');
        if (typeof el.pop.hidePopover === 'function' && el.pop.hasAttribute('popover')) {
            try { el.pop.hidePopover(); } catch (_) { }
        }
        state.sel = -1;
        markSelection();
    }

    function stepBlock(d) {
        var item = state.files[state.active];
        if (!item || !item.blocks || !item.blocks.length) return;
        var n = item.blocks.length;
        var i = ((state.sel < 0 ? 0 : state.sel + d) % n + n) % n;
        selectBlock(i, el.hits && el.hits.children[i]);
    }

    /** Editable list of what was found — the keyboard path and the overview. */
    function renderBlockList(item) {
        if (!el.blocks) return;
        el.blocks.innerHTML = '';
        if (el.blocksN) {
            el.blocksN.textContent = t('blocks_n', ':n').split(':n').join(fmtN(item.blocks.length));
        }
        item.blocks.forEach(function (b, i) {
            // Строка — контейнер с ДВУМЯ кнопками, а не кнопка с кнопкой внутри:
            // вложенный орган управления не получает ни правильной роли, ни
            // предсказуемой клавиатуры.
            var row = document.createElement('div');
            row.className = 'pt-blk' + (b.hidden ? ' is-off' : '');

            var pick = document.createElement('button');
            pick.type = 'button';
            pick.className = 'pt-blk-pick';

            var orig = document.createElement('span');
            orig.className = 'pt-blk-o';
            orig.textContent = b.text;

            var val = document.createElement('span');
            val.className = 'pt-blk-v';
            val.textContent = b.edited != null ? b.edited : (b.translated || '');

            pick.appendChild(orig);
            pick.appendChild(val);
            pick.addEventListener('click', function () { selectBlock(i, row); });

            var off = document.createElement('button');
            off.type = 'button';
            off.className = 'pt-blk-x';
            off.title = b.hidden ? t('block_restore', 'Вернуть перевод') : t('block_skip', 'Не переводить этот фрагмент');
            off.setAttribute('aria-pressed', b.hidden ? 'true' : 'false');
            off.textContent = b.hidden ? '↺' : '×';
            off.addEventListener('click', function (ev) {
                ev.stopPropagation();
                toggleBlock(item, i);
            });

            row.appendChild(pick);
            row.appendChild(off);
            el.blocks.appendChild(row);
        });
        markSelection();
    }

    function toggleBlock(item, i) {
        var b = item.blocks[i];
        b.hidden = !b.hidden;
        funnel('block_toggle', { off: b.hidden });
        syncSkipButton(b);
        scheduleRecompose(item);
        renderBlockList(item);
    }

    function refreshRow(item, i) {
        if (!el.blocks) return;
        var row = el.blocks.children[i];
        if (!row) return;
        var b = item.blocks[i];
        var val = row.querySelector('.pt-blk-v');
        if (val) val.textContent = b.edited != null ? b.edited : (b.translated || '');
    }

    var recomposeTimer = null;
    function scheduleRecompose(item) {
        clearTimeout(recomposeTimer);
        recomposeTimer = setTimeout(function () {
            compose(item);
            state.compare = false;
            syncCompare();
            paintResult(item);
        }, 180);
    }

    function syncCompare() {
        if (!el.compare) return;
        el.compare.setAttribute('aria-pressed', state.compare ? 'true' : 'false');
        if (el.compareLabel) {
            el.compareLabel.textContent = state.compare
                ? t('show_translation', 'Показать перевод')
                : t('show_original', 'Показать оригинал');
        }
    }

    // ── output ───────────────────────────────────────────────────────────

    function outputName(item, ext) {
        var base = (item.name || 'image').replace(/\.[^.]+$/, '');
        var tgt = el.tgt.value;
        if (window.__outputName && window.__outputName.sanitize) {
            return window.__outputName.sanitize(base + '-' + tgt + '.' + ext);
        }
        return base + '-' + tgt + '.' + ext;
    }

    async function download(item, type) {
        if (!item) return;
        var cv = item.canvas || item.base;
        var mime = type === 'jpg' ? 'image/jpeg' : 'image/png';
        var blob = await new Promise(function (res) { cv.toBlob(res, mime, 0.94); });
        if (!blob) { toast(t('err_export', 'Не удалось сохранить изображение'), 'error'); return; }
        if (window.__downloadFile) window.__downloadFile(blob, outputName(item, type));
        funnel('download', { type: type, w: cv.width, h: cv.height });
        if (window.__aeToolComplete) {
            window.__aeToolComplete({ ok: true, blocks: item.blocks ? item.blocks.length : 0, type: type });
        }
        if (state.limitTicket) { try { state.limitTicket.commit(); } catch (_) { } state.limitTicket = null; }
    }

    function copyText(item, which) {
        if (!item) return;
        var lines = (item.blocks || []).map(function (b) {
            return which === 'orig' ? b.text : (b.edited != null ? b.edited : b.translated || '');
        }).filter(Boolean);
        var text = lines.join('\n');
        navigator.clipboard.writeText(text).then(function () {
            toast(t('copied', 'Скопировано'), 'success');
            funnel('copy_text', { which: which, chars: text.length });
            // A copied TRANSLATION is a delivered result just as much as a saved
            // picture — plenty of people want the text, not the image. Copying
            // the ORIGINAL is not: that is what OCR alone would have given.
            if (which !== 'orig' && state.limitTicket) {
                try { state.limitTicket.commit(); } catch (_) { }
                state.limitTicket = null;
            }
        }, function () {
            toast(t('err_copy', 'Не удалось скопировать'), 'error');
        });
    }

    // ── intake ───────────────────────────────────────────────────────────

    async function accept(files) {
        // Two runs at once would share one ORT worker and one `state.engine`;
        // the second `ensureEngine` for a different language closes the worker
        // the first is still reading through, and the tab hangs with no error.
        if (state.busy) return;

        var list = Array.prototype.slice.call(files).filter(function (f) {
            return /^image\//.test(f.type) || /\.(png|jpe?g|webp|heic|heif|bmp)$/i.test(f.name);
        });
        if (!list.length) { toast(t('err_format', 'Нужен файл изображения'), 'error'); return; }

        // Cap is charged per FILE, and only once a result is actually handed
        // over — a failed read must not cost the visitor a slot.
        if (window.__toolLimit) {
            var keys = list.map(function (f) { return f.name + ':' + f.size; });
            state.limitTicket = await window.__toolLimit.begin('photo-translate', keys);
            if (!state.limitTicket) return;
        }

        closeEditor();
        state.files = list.map(function (f, i) { return { id: i, file: f, name: f.name }; });
        state.active = 0;
        funnel('file_picked', { n: list.length, bytes: list[0].size });
        stage('uploading', {});

        showWork();
        state.busy = true;
        var batch = state.files.length > 1;
        try {
            for (var i = 0; i < state.files.length; i++) {
                state.active = i;
                if (batch) {
                    // Keep the progress view for the whole batch. Flashing each
                    // finished picture and then snatching it away for the next
                    // file reads as a glitch, and it also hides how much is left.
                    showWork();
                    setPhase('batch', t('phase_batch', 'Файл') + ' ' + (i + 1) + '/' + state.files.length);
                }
                await runFile(state.files[i], !batch);
            }
            if (batch) {
                // Land on the FIRST picture: it is the one the visitor chose
                // first and expects to see, and the strip holds the rest.
                var firstDone = state.files.filter(function (f) { return f.done; })[0];
                if (firstDone) {
                    state.active = state.files.indexOf(firstDone);
                    showResult(firstDone);
                } else {
                    showNoText(state.files[0]);
                }
            }
        } catch (e) {
            fail(e, 'photo-translate');
            funnel('run_failed', { message: String(e && e.message || e).slice(0, 120) });
            setPhase('error', t('phase_error', 'Не получилось'));
            if (state.limitTicket) { try { state.limitTicket.abort(); } catch (_) { } state.limitTicket = null; }
            backToUpload();
        } finally {
            state.busy = false;
        }
    }

    function backToUpload() {
        closeEditor();
        setView('intake');
    }

    // ── language controls ────────────────────────────────────────────────

    function syncSwap() {
        if (!el.swap) return;
        // Nothing to swap while the source is "detect automatically", and
        // nothing to swap INTO a language the recogniser cannot read.
        var canSwap = el.src.value !== 'auto'
            && !!el.tgt.querySelector('option[value="' + el.src.value + '"]')
            && !!el.src.querySelector('option[value="' + el.tgt.value + '"]');
        el.swap.disabled = !canSwap;
    }

    /** Вид сегмента рисует CSS по `:checked` — здесь только состояние и память. */
    function setQuality(q, silent) {
        var r = $(q === 'precise' ? 'ptQualityPrecise' : 'ptQualityStandard');
        if (r) r.checked = true;
        if (!silent && window.__toolSettings) window.__toolSettings.save('photo-translate', 'quality', q);
    }

    // ── camera ───────────────────────────────────────────────────────────

    function openCamera() {
        if (!window.PTCamera) { toast(t('phase_error', ''), 'error'); return; }
        setView('camera');
        if (!state.camera) {
            state.camera = window.PTCamera.create({
                app: el.app,
                host: $('ptCam') || (el.app && el.app.querySelector('.pt-cam')),
                video: $('ptCamVideo'),
                overlay: $('ptCamOverlay'),
                stateEl: $('ptCamState'),
                failEl: $('ptCamFail'),
                scanBtn: $('ptCamScan'),
                flipBtn: $('ptCamFlip'),
                i18n: T,
                ensureEngine: ensureEngine,
                srcLang: function () { return el.src.value; },
                tgtLang: function () { return el.tgt.value; },
                onLangLocked: function (lang) {
                    // The decision is shown, not hidden: the same rule as the
                    // still-photo path, where "read as" is an explicit control.
                    if (el.src.value === 'auto' && el.src.querySelector('option[value="' + lang + '"]')) {
                        el.src.value = lang;
                        syncSwap();
                    }
                },
            });
        }
        state.camera.resetLang();
        state.camera.start();
        funnel('camera_open', {});
    }

    function closeCamera(next) {
        if (state.camera) state.camera.stop();
        setView(next || 'intake');
    }

    async function shootCamera() {
        if (!state.camera) return;
        // Кадр снимаем СРАЗУ — человек нажал на то, что видит сейчас, и ждать
        // конца прохода значит снять уже другое.
        var file = await state.camera.shoot();
        // no frame at the press (iOS interrupts capture): the camera stays, the next press works
        if (!file) { toast(window.__cameraNoFrame(), 'warning'); return; }
        funnel('camera_shot', {});
        state.camera.stop();
        // …а вот ЧИТАТЬ начинаем только когда камера отпустила общий воркер:
        // иначе снимок встаёт в очередь за проходом по кадру, который уже
        // никому не нужен.
        await state.camera.idle();
        accept([file]);
    }

    // ── wiring ───────────────────────────────────────────────────────────

    function init() {
        el.app = $('ptApp');
        el.phase = $('ptPhase');
        el.bar = $('ptBar');
        el.live = $('ptLive');
        el.work = el.app && el.app.querySelector('.pt-work');
        el.canvas = $('ptCanvas');
        el.view = $('ptView');
        el.figure = $('ptFigure');
        el.hits = $('ptHits');
        el.empty = $('ptEmpty');
        el.actions = $('ptActions');
        el.blocks = $('ptBlocks');
        el.blocksN = $('ptBlocksN');
        el.src = $('ptSrc');
        el.tgt = $('ptTgt');
        el.swap = $('ptSwap');
        el.compare = $('ptCompare');
        el.compareLabel = $('ptCompareLabel');
        el.strip = $('ptStrip');
        el.stripPanel = $('ptStripPanel');
        el.railResult = $('ptRailResult');
        el.readAs = $('ptReadAs');
        el.readLang = $('ptReadLang');
        el.zoomVal = $('ptZoomVal');
        el.pop = $('ptPop');
        el.popOrig = $('ptPopOrig');
        el.popText = $('ptPopText');
        el.popSkip = $('ptPopSkip');

        if (!el.app || !el.src) return;

        if (el.hits) { el.hits.style.position = 'absolute'; el.hits.style.inset = '0'; }

        // Где Popover API нет, атрибут всё равно остаётся в разметке — а значит
        // и UA-правило `display:none` не появляется, и элемент виден всегда, и
        // запасной селектор `:not([popover])` не совпадает НИКОГДА. Снимаем его
        // ровно тогда, когда он ничего не означает: дальше редактор работает
        // обычным классом, а позицию в обоих путях считает один и тот же код.
        if (el.pop && typeof el.pop.showPopover !== 'function') el.pop.removeAttribute('popover');

        if (window.__toolSettings) {
            window.__toolSettings.bind('photo-translate', el.src, 'src', 'auto');
            window.__toolSettings.bind('photo-translate', el.tgt, 'tgt', (window.__LOCALE || 'en').split('-')[0]);
        }
        syncSwap();
        syncCompare();

        if (el.swap) {
            el.swap.addEventListener('click', function () {
                var a = el.src.value, b = el.tgt.value;
                el.src.value = b;
                el.tgt.value = a;
                syncSwap();
                funnel('lang_swap', { src: b, tgt: a });
                if (window.__toolSettings) {
                    window.__toolSettings.save('photo-translate', 'src', b);
                    window.__toolSettings.save('photo-translate', 'tgt', a);
                }
                var item = state.files[state.active];
                if (item && item.base) reread(item, b);
            });
        }

        if (LANE) {
            Array.prototype.forEach.call(document.querySelectorAll('input[name="ptQuality"]'), function (r) {
                r.addEventListener('change', function () {
                    if (!r.checked) return;
                    var note = $('ptPreciseNote');
                    if (r.value === 'precise' && !preciseOpen()) {
                        setQuality('standard');
                        upsellNote(note);
                        return;
                    }
                    if (note) { note.hidden = true; note.textContent = ''; }
                    setQuality(r.value);
                    funnel('quality_pick', { q: r.value });
                });
            });
            var pb = $('ptPrecise');
            if (pb) pb.addEventListener('click', translateItemPrecisely);
            // План посетителя приходит из session-bootstrap ПОСЛЕ старта скрипта.
            var syncQ = function () { syncPlanBadge(); applySavedQuality(); };
            syncQ();
            window.addEventListener('tier-bootstrap:ready', syncQ);
            document.addEventListener('tier-bootstrap:ready', syncQ);
        }

        if (VISION) {
            var vb = $('ptVision');
            if (vb) vb.addEventListener('click', readWithVision);
            // План посетителя приходит из session-bootstrap ПОСЛЕ старта скрипта.
            var syncV = function () { var p = $('ptVisionPlan'), b2 = $('ptVision'); if (p) p.hidden = !b2 || b2.hidden || isPro(); };
            window.addEventListener('tier-bootstrap:ready', syncV);
            document.addEventListener('tier-bootstrap:ready', syncV);
        }

        var sameGo = $('ptSameGo');
        if (sameGo) sameGo.addEventListener('click', function () {
            var pick = $('ptSameTgt');
            if (!pick || !pick.value || !el.tgt) return;
            funnel('same_language_picked', { to: pick.value });
            el.tgt.value = pick.value;
            syncSwap();
            // Тот же путь, что ручная смена языка: перевод без повторного чтения.
            el.tgt.dispatchEvent(new Event('change'));
        });

        // Bind through the shared primitive, never a raw addEventListener: it
        // also drains `el.__pendingFile` when a file was picked before this
        // script attached, which is the race that loses the very first drop.
        var up = $('ptUpload');
        if (up) {
            // The primitive calls back with (File, detail) — NOT with an event.
            // Reading `ev.detail` off the File yields undefined and the intake
            // then does nothing at all, silently: the upload appears to work and
            // the tool never starts. Cost of learning that: one whole E2E round.
            var onFile = function (file, detail) {
                var d = detail || {};
                var files = d.files && d.files.length ? d.files : (file ? [file] : null);
                if (files && files.length) accept(files);
            };
            if (typeof window.__bindUploaderFile === 'function') {
                window.__bindUploaderFile(up, onFile);
            } else {
                // Fallback keeps the SAME callback shape, so the two paths can
                // never drift apart the way the first version of this did.
                up.addEventListener('tool-uploader:file', function (ev) {
                    var d = (ev && ev.detail) || {};
                    onFile(d.file || (d.files && d.files[0]) || null, d);
                });
            }
            up.addEventListener('tool-uploader:clear', function () {
                state.files = [];
                backToUpload();
            });
        }

        var dl = $('ptDownload');
        if (dl) dl.addEventListener('click', function () { download(state.files[state.active], 'png'); });
        var dlj = $('ptDownloadJpg');
        if (dlj) dlj.addEventListener('click', function () { download(state.files[state.active], 'jpg'); });
        var cp = $('ptCopy');
        if (cp) cp.addEventListener('click', function () { copyText(state.files[state.active], 'tr'); });
        var cpo = $('ptCopyOrig');
        if (cpo) cpo.addEventListener('click', function () { copyText(state.files[state.active], 'orig'); });
        var again = $('ptAgain');
        if (again) again.addEventListener('click', backToUpload);

        // ── zoom / pan ───────────────────────────────────────────────────
        wireView();
        var zi = $('ptZoomIn'), zo = $('ptZoomOut'), zf = $('ptZoomFit');
        if (zi) zi.addEventListener('click', function () { zoomTo(state.zoom * 1.3); });
        if (zo) zo.addEventListener('click', function () { zoomTo(state.zoom / 1.3); });
        if (zf) zf.addEventListener('click', function () { fitView(); closeEditor(); });

        if (el.compare) {
            el.compare.addEventListener('click', function () {
                state.compare = !state.compare;
                syncCompare();
                closeEditor();
                paintResult(state.files[state.active]);
                funnel('compare_toggle', { orig: state.compare });
            });
        }

        // ── editor popover ───────────────────────────────────────────────
        if (el.popText) {
            el.popText.addEventListener('input', function () {
                var item = state.files[state.active];
                if (!item || state.sel < 0) return;
                item.blocks[state.sel].edited = el.popText.value;
                refreshRow(item, state.sel);
                scheduleRecompose(item);
            });
            el.popText.addEventListener('keydown', function (e) {
                if (e.key === 'Escape') { closeEditor(); return; }
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { closeEditor(); }
            });
        }
        if (el.popSkip) {
            el.popSkip.addEventListener('click', function () {
                var item = state.files[state.active];
                if (!item || state.sel < 0) return;
                toggleBlock(item, state.sel);
            });
        }
        var pn = $('ptPopNext'), pp = $('ptPopPrev');
        if (pn) pn.addEventListener('click', function () { stepBlock(1); });
        if (pp) pp.addEventListener('click', function () { stepBlock(-1); });
        if (el.pop) {
            el.pop.addEventListener('toggle', function (e) {
                // `toggle` приходит асинхронно, а openEditor закрывает поповер
                // ПЕРЕД тем, как открыть его на соседнем блоке: без сверки с
                // живым состоянием это событие прилетало уже после выбора и
                // снимало его — рамка гасла на только что нажатой надписи.
                if (e.newState !== 'closed') return;
                try { if (el.pop.matches(':popover-open')) return; } catch (_) { }
                state.sel = -1;
                markSelection();
                el.pop.classList.remove('is-open');
            });
        }
        // Light dismiss for the no-popover path.
        document.addEventListener('pointerdown', function (e) {
            if (!el.pop || !el.pop.classList.contains('is-open')) return;
            if (el.pop.hasAttribute('popover')) return;   // the browser handles it
            if (el.pop.contains(e.target) || (e.target.closest && e.target.closest('.pt-hit,.pt-blk'))) return;
            closeEditor();
        });

        // ── camera ───────────────────────────────────────────────────────
        var camOpen = $('ptCamOpen');
        if (camOpen) camOpen.addEventListener('click', openCamera);
        var camClose = $('ptCamClose');
        if (camClose) camClose.addEventListener('click', function () { closeCamera('intake'); });
        var camShot = $('ptCamShot');
        if (camShot) camShot.addEventListener('click', shootCamera);
        var camFlip = $('ptCamFlip');
        if (camFlip) camFlip.addEventListener('click', function () { if (state.camera) state.camera.flip(); });
        var camScan = $('ptCamScan');
        if (camScan) camScan.addEventListener('click', function () { if (state.camera) state.camera.scanOnce(); });

        // Camera offered only where one exists. Feature-detected, not sniffed —
        // a "Take a photo" button on a desktop without a camera is a promise
        // that cannot be kept.
        if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices().then(function (list) {
                if (list.some(function (d) { return d.kind === 'videoinput'; })) {
                    el.app.classList.add('pt-has-camera');
                }
            }).catch(function () { });
        }
        // A live viewfinder must not keep running in a tab nobody is looking at.
        window.addEventListener('pagehide', function () {
            if (state.camera && state.camera.isRunning()) closeCamera('intake');
        });
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'visible') return;
            if (state.camera && state.camera.isRunning()) closeCamera('intake');
        });

        var refit = null;
        window.addEventListener('resize', function () {
            clearTimeout(refit);
            refit = setTimeout(function () {
                var item = state.files[state.active];
                if (item && item.done && el.app.dataset.state === 'result') {
                    closeEditor();
                    fitView();
                }
            }, 120);
        });

        // Re-translate when the target changes, without re-reading the picture.
        if (el.tgt) {
            el.tgt.addEventListener('change', function () {
                syncSwap();
                var item = state.files[state.active];
                if (!item || !item.blocks) return;
                retranslate(item);
            });
        }
        if (el.src) el.src.addEventListener('change', syncSwap);

        // Re-read when the user corrects the source language. The picture is
        // already decoded — only recognition and translation run again.
        if (el.readLang) {
            el.readLang.addEventListener('change', function () {
                var item = state.files[state.active];
                if (!item || !item.base) return;
                reread(item, el.readLang.value);
            });
        }

        stage('idle', {});
    }

    /** Read the same picture again with a different source language. */
    async function reread(item, lang) {
        // Same re-entry hazard as accept(): switching language closes the worker
        // an in-flight read is still using.
        if (state.busy) return;
        state.busy = true;
        closeEditor();
        showWork();
        funnel('reread_lang', { from: item.readLang, to: lang });
        try {
            // Decode from the ORIGINAL file, not from the working canvas.
            // A canvas round-trip goes through colour management and comes back
            // a few units off, which is enough to change a marginal read:
            // measured, the same picture gave "улица ленина" from the file and
            // "улица LeHHa" from the canvas with the identical model.
            var bmp = await createImageBitmap(item.file);
            var res = await readWith(item, lang, bmp);
            item.result = res;
            item.readLang = lang;
            if (!res.lines.length) { showNoText(item); return; }
            item.blocks = window.PTCompose.groupBlocks(res.lines);
            item.srcDetected = lang;
            await translateBlocks(item, lang, el.tgt.value, item.mode);
            compose(item);
            showResult(item);
        } catch (e) {
            fail(e, 'photo-translate:reread');
            setPhase('error', t('phase_error', 'Не получилось'));
        } finally {
            state.busy = false;
        }
    }

    /**
     * «Точный перевод» снимка: все надписи — ОДНИМ запросом и в порядке чтения.
     * Меню и вывески состоят из коротких блоков, и поодиночке «Grilled» и
     * «salmon» переводятся двумя несвязанными словами; вместе модель видит
     * блюдо. Возвращает блоки, оставшиеся обычному лейну.
     */
    async function translateBlocksPrecise(item, srcCode, tgtLang, ctrl) {
        var blocks = item.blocks, send = [], rest = [], total = 0;
        blocks.forEach(function (b) {
            if (!b.text || b.text.length > LANE.unitMax) rest.push(b);
            else { send.push(b); total += b.text.length; }
        });
        if (!send.length) return blocks;
        if (total > LANE.maxChars || send.length > LANE.maxUnits) {
            item.preciseMsg = t('precise_too_long', '').split(':chars').join(fmtN(total)).split(':max').join(fmtN(LANE.maxChars));
            funnel('precise_refused', { reason: 'too_long', chars: total });
            return blocks;
        }
        setPhase('translate', t('precise_phase', 'Точный перевод надписей'));
        funnel('precise_start', { blocks: send.length, chars: total });
        var t0 = Date.now();
        try {
            if (window.__lanePay) window.__lanePay.hide($('ptPrecisePay'));
            var res = await window.__translateLane.translate({
                serverUrl: LANE.serverUrl,
                texts: send.map(function (b) { return b.text; }),
                src: srcCode || 'auto', tgt: tgtLang,
                signal: ctrl.signal,
                pay: !!(window.__lanePay && window.__lanePay.agreed('photo-translate')),
            });
            if (res.paid_tokens > 0 && window.__lanePay) {
                window.__lanePay.paid($('ptPrecisePay'), res);
                toast(res.paid_text, 'info');
            }
            var bad = {};
            res.failed.forEach(function (i) { bad[i] = true; });
            send.forEach(function (b, i) {
                if (bad[i] || !res.texts[i]) { rest.push(b); return; }
                b.edited = null;
                b.paint = null;
                b.translated = res.texts[i];
                b.translateFailed = false;
            });
            item.precise = true;
            item.preciseMsg = rest.length ? t('precise_partial', '').split(':n').join(fmtN(rest.length)) : '';
            funnel('precise_done', { blocks: send.length, failed: res.failed.length, ms: Date.now() - t0 });
            return rest;
        } catch (e) {
            if (e && e.name === 'AbortError') throw e;
            // Отказ с ценой — предложение «за N токенов», а не ошибка: снимок уже
            // переведён обычным способом, кнопка запускает точный повтор.
            if (e && e.data && window.__lanePay
                && window.__lanePay.show($('ptPrecisePay'), e.data, { slug: 'photo-translate', run: translateItemPrecisely })) {
                funnel('precise_refused', { status: e.status || 0, pay: 1 });
                return blocks;
            }
            if (e && e.premium) item.preciseUpsell = true;
            else item.preciseMsg = (e && e.message && e.status !== 502 ? e.message + ' · ' : '')
                + t('precise_fallback', 'Точный перевод не получился — надписи переведены обычным способом');
            funnel('precise_refused', { status: e ? e.status : 0 });
            return blocks;
        }
    }

    function renderSameLang(item) {
        var box = $('ptSame');
        if (!box) return;
        box.hidden = !(item && item.sameLang);
        if (box.hidden) return;
        var opt = el.src && el.src.querySelector('option[value="' + item.readLang + '"]');
        var name = opt ? opt.textContent.trim() : item.readLang;
        if (name.slice(-1) === '*') name = name.slice(0, -1).trim();
        $('ptSameMsg').textContent = t('same_lang', 'Язык надписей совпадает с языком перевода (:lang). На какой язык перевести?').split(':lang').join(name);
        var pick = $('ptSameTgt');
        pick.innerHTML = '';
        Array.prototype.forEach.call(el.tgt.options, function (o) {
            if (o.value !== item.readLang) pick.appendChild(new Option(o.textContent, o.value));
        });
        if (item.readLang !== 'en' && pick.querySelector('option[value="en"]')) pick.value = 'en';
    }

    // ── «Рукописный и сложный шрифт» ───────────────────────────────────────

    function visionSay(text, kind) {
        var note = $('ptVisionNote');
        if (!note) return;
        note.classList.remove('good', 'warn');
        if (kind) note.classList.add(kind);
        note.textContent = text || '';
        note.hidden = !text;
    }

    function visionUpsell() {
        var note = $('ptVisionNote');
        if (!note || !VISION) return;
        var plan = VISION.plan || 'Premium';
        visionSay(t('vision_upsell', 'Чтение рукописного текста ИИ входит в :plan').split(':plan').join(plan) + ' ', 'warn');
        if (VISION.pricingUrl) {
            var a = document.createElement('a');
            a.href = VISION.pricingUrl;
            a.target = '_blank';
            a.rel = 'noopener';
            a.textContent = t('precise_see_plan', 'Что даёт :plan').split(':plan').join(plan);
            note.appendChild(a);
        }
        funnel('vision_upsell', {});
    }

    function renderVision(item) {
        var show = !!(VISION && window.PTVision && item && item.blocks && item.blocks.length);
        var btn = $('ptVision'), hint = $('ptVisionHint'), plan = $('ptVisionPlan');
        if (btn) btn.hidden = !show;
        if (hint) hint.hidden = !show;
        if (plan) plan.hidden = !show || isPro();
        if (!show || !item.vision) visionSay('', '');
    }

    /**
     * The photo, with the lines the browser found drawn as numbered frames, goes to
     * the model; each frame it reads replaces the browser's reading of that block.
     * Only on this button, only for Premium — the one path that sends the photo.
     */
    async function readWithVision() {
        var item = state.files[state.active];
        if (!VISION || !window.PTVision || !item || !item.base || !item.blocks || !item.blocks.length) return;
        if (!isPro()) { visionUpsell(); return; }
        if (state.busy) return;
        var usable = item.blocks.filter(function (b) { return !b.hidden; });
        if (!usable.length) { visionSay(t('vision_none', 'На снимке не нашлось строк, которые можно отметить для ИИ.'), 'warn'); return; }

        state.busy = true;
        var btn = $('ptVision');
        if (btn) btn.disabled = true;
        visionSay(t('vision_phase', 'ИИ читает надписи') + '…', '');
        funnel('vision_start', { blocks: usable.length, mob: MOB });
        var t0 = performance.now();
        try {
            var prepared = await window.PTVision.prepare(item.base, item.blocks, VISION.maxEdge);
            // The person's own choice of source language is the hint; a guess made by
            // "auto" is not worth biasing the model with.
            var data = await window.PTVision.send(VISION, prepared, el.src.value, el.tgt.value);
            var n = window.PTVision.apply(item.blocks, data.regions);
            if (!n) { var none = new Error('empty'); none.status = 502; throw none; }
            item.vision = true;
            closeEditor();
            compose(item);
            showResult(item);
            visionSay(t('vision_done', 'Надписи прочитаны ИИ — сверьте со снимком, любую строку можно поправить.'), 'good');
            funnel('vision_done', { blocks: n, of: usable.length, left: data.left, ms: Math.round(performance.now() - t0), mob: MOB });
        } catch (e) {
            if (e && e.premium) visionUpsell();
            else visionSay((e && e.status && e.status !== 502 && e.message) ? e.message
                : t('vision_failed', 'ИИ не смог прочитать этот снимок — остался результат браузера.'), 'warn');
            funnel('vision_failed', { status: (e && e.status) || 0, mob: MOB });
        } finally {
            state.busy = false;
            if (btn) btn.disabled = false;
        }
    }

    function renderPrecise(item) {
        var note = $('ptPreciseNote'), btn = $('ptPrecise');
        if (btn) btn.hidden = !LANE || !item || !!item.precise;
        if (!note) return;
        note.hidden = true;
        note.textContent = '';
        note.classList.remove('good', 'warn');
        if (!item) return;
        if (item.preciseUpsell) { upsellNote(note); return; }
        if (item.preciseMsg) { note.textContent = item.preciseMsg; note.classList.add('warn'); note.hidden = false; return; }
        if (item.precise) {
            note.textContent = t('precise_done', 'Точный перевод: надписи переведены вместе');
            note.classList.add('good');
            note.hidden = false;
        }
    }

    /** «Перевести точнее» для открытого снимка — без повторного чтения. */
    async function translateItemPrecisely() {
        var item = state.files[state.active];
        if (!item || !item.blocks || state.busy) return;
        if (!preciseOpen()) { upsellNote($('ptPreciseNote')); return; }
        state.busy = true;
        closeEditor();
        showWork();
        try {
            item.preciseMsg = '';
            item.preciseUpsell = false;
            await translateBlocks(item, item.srcDetected || item.readLang, el.tgt.value, 'precise', true);
            item.mode = item.precise ? 'precise' : 'standard';
            compose(item);
        } catch (e) {
            fail(e, 'photo-translate:precise');
        } finally {
            state.busy = false;
            showResult(item);
        }
    }

    /** Translate every block of an item; shared by the first run and re-reads. */
    async function translateBlocks(item, srcCode, tgtLang, mode, again) {
        var ctrl = new AbortController();
        state.abort = ctrl;
        var blocks = item.blocks;
        var todo = blocks;
        if (!again) { item.precise = false; item.preciseMsg = ''; item.preciseUpsell = false; }
        // Надписи уже на языке перевода: перевод «в себя» — пустая работа (а у
        // точного лейна ещё и серверный прогон). Результат задаст вопрос о языке.
        item.sameLang = !!srcCode && srcCode === tgtLang;
        if (item.sameLang) {
            blocks.forEach(function (b) { b.translated = b.text; b.translateFailed = false; b.edited = null; b.paint = null; });
            funnel('same_language_asked', { lang: srcCode });
            return;
        }
        if (mode === 'precise' && LANE && window.__translateLane) {
            todo = await translateBlocksPrecise(item, srcCode, tgtLang, ctrl);
            // Повтор «точнее»: чего модель не взяла, уже переведено в первый раз.
            if (again) todo = [];
        }
        // Все надписи снимка уходят движку РАЗОМ (translateTexts — один запрос
        // на снимок), а не по запросу на надпись друг за другом: страница
        // меню — это 30–40 надписей, и каждая стоила своего ожидания.
        // ⚠️ `reason` в ответе значит, что надпись НЕ переведена, а в `text`
        // лежит оригинал. До 14.10.2026 провал определялся по пустому тексту,
        // и такая надпись уходила на снимок как «перевод».
        todo.forEach(function (b) { b.edited = null; b.paint = null; });
        if (todo.length) {
            try {
                var res = await window.TranslatorEngine.translateTexts(
                    todo.map(function (b) { return b.text; }), srcCode, tgtLang, ctrl.signal, {
                        onProgress: function (p) { setProgress(0.5 + 0.45 * p.frac); },
                    });
                todo.forEach(function (b, i) {
                    var r = res[i] || { reason: 'unknown' };
                    b.translateFailed = !!r.reason || !r.text;
                    b.translated = b.translateFailed ? b.text : r.text;
                });
            } catch (e) {
                todo.forEach(function (b) { b.translated = b.text; b.translateFailed = true; });
            }
            setProgress(0.95);
        }
        var failedN = blocks.filter(function (x) { return x.translateFailed; }).length;
        funnel('translate_done', { blocks: blocks.length, failed: failedN, src: srcCode, tgt: tgtLang });
        if (failedN === blocks.length) {
            toast(t('err_translate', 'Не удалось перевести текст'), 'error');
        }
    }

    /** Target language changed: translate again, but do NOT re-read the photo. */
    async function retranslate(item) {
        if (state.busy) return;
        state.busy = true;
        closeEditor();
        showWork();
        setPhase('translate', t('phase_translate', 'Перевод'));
        var tgt = el.tgt.value;
        try {
            await translateBlocks(item, item.srcDetected || item.readLang, tgt, item.mode);
            compose(item);
            showResult(item);
            funnel('retranslate', { tgt: tgt, blocks: item.blocks.length });
        } catch (e) {
            fail(e, 'photo-translate:retranslate');
            showResult(item);
        } finally {
            state.busy = false;
        }
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
